import { inject, Injectable } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { environment } from '@env/environment';
import moment from 'moment';

/**
 *  ### **IMetaTags**
 *
 * Las interfaces son el mecanismo que usa Typescript para definir tipos en las clases, en este caso es la
 * Interfaz que contendra la estructura de los datos para los metaTags*/
export interface IMetaTags {
  /** Atributo que guarda el autor del proyecto*/
  author: string;
  /** Atributo que guarda las palabras clave*/
  keywords: string;
  /** Atributo que guarda la fecha*/
  date: string;
  /** Atributo que guarda el titulo*/
  title: string;
  /** Atributo que guarda el tipo*/
  type: string;
  /** Atributo que guarda*/
  charset: string;
  /** Atributo que guarda la descripción*/
  description: string;
  /** Atributo que guarda las imagenes*/
  image: string;
  /** Atributo que guarda la url del contenido*/
  url: string;
}
/**
 * ### **SeoService**
 *
 * Un Servicio en Angular es el mecanismo para compartir funcionalidad entre componentes, en este caso se encarga de compartir las funciones
 * para el SEO, estas hacen diferentes peticiones o llamados a distintos API's que genera el backend.
 */
@Injectable({
  providedIn: 'root',
})
export class Seo {
  private readonly meta = inject(Meta);

  /**
   * Función constructora encargada de agregar los datos o tags, para esto inyecta la dependencia Meta.
   * @param meta Paratametro que le da acceso a las funcionalidades de meta.
   */
  constructor() {
    this.addMetaTags();
  }

  /** Función encargada de definir los tags que iran en las etiquetas meta para la indexación en google*/
  addMetaTags(): void {
    const date = new Date();
    this.meta.addTags([
      { property: 'title', content: 'De Tour Cundinamarca' },
      { property: 'og:title', content: 'De Tour Cundinamarca' },
      {
        name: 'description',
        content: 'Departamento de Cundinamarca',
      },
      { property: 'og:description', content: 'Departamento de Cundinamarca' },
      {
        name: 'keywords',
        content:
          'De Tour Cundinamarca, turismo Cundinamarca, paquetes turísticos, lugares para visitar, experiencias en Cundinamarca',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      },
      { property: 'fb:app_id', content: environment.facebookAppId },
      { name: 'robots', content: 'INDEX, FOLLOW' },
      { name: 'author', content: 'GrupoCato' },
      {
        name: 'date',
        content: moment(date).format('YYYY-MM-DD'),
        scheme: 'YYYY-MM-DD',
      },
      { httpEquiv: 'Content-Type', content: 'text/html' },
      { property: 'og:type', content: 'website' },

      { property: 'og:image', content: '' },
      { property: 'og:image:alt', content: '' },
      { property: 'og:url', content: '' },
      { charset: 'UTF-8' },
    ]);
  }

  /** Función encargada de actualizar los tags*/
  updateMetaTags(metaTags: Partial<IMetaTags>): void {
    this.meta.updateTag(
      {
        name: 'description',
        content: metaTags?.description || '',
      },
      "name='description'",
    );
    // this.meta.updateTag({ name: 'keywords', content: metaTags?.keywords || '' }, "name='keywords'");
    this.meta.updateTag(
      {
        name: 'date',
        content: metaTags?.date || '',
        scheme: 'YYYY-MM-DD',
      },
      "name='date'",
    );
    this.meta.updateTag({ name: 'author', content: 'GrupoCato' });
    this.meta.updateTag(
      { name: 'title', content: metaTags?.title || 'De Tour Cundinamarca' },
      "name='title'",
    );
    this.meta.updateTag(
      {
        property: 'og:title',
        content: metaTags?.title || 'De Tour Cundinamarca',
      },
      "name='title'",
    );
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'type', content: 'website' });
    this.meta.updateTag(
      {
        property: 'og:description',
        content: metaTags?.description || '',
      },
      "property='og:description'",
    );
    this.meta.updateTag(
      {
        property: 'og:image',
        content: metaTags?.image || '',
      },
      "property='og:image'",
    );
    this.meta.updateTag(
      {
        property: 'og:image:alt',
        content: metaTags?.title || 'De Tour Cundinamarca',
      },
      "property='og:image:alt'",
    );
    this.meta.updateTag({ property: 'og:url', content: metaTags?.url || '' }, "property='og:url'");
  }
}
